/** The private reflection (P9): read, and save one field at a time. Saved fields come back through the cache, so every view of the round agrees. */
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ContentCatalog, Reflection } from "@bsim/contracts";
import type { Decisions, DecisionFamily } from "@bsim/domain";
import { changeLines, changedEntries } from "../decisions/rationale/changeLines";
import { lowerFirst } from "../format/labels";
import { useApi, useRoute } from "../api/context";
import { errorSentence } from "../errors";
import { useToast } from "../theme/Toast";

export type ReflectionField = "surprised" | "differently" | "commitment";
export type SaveState = "idle" | "saving" | "saved" | "failed";

/** What the round's question may depend on: the families the team changed and the cards that fired for its bank. */
export interface ReflectionContext {
  readonly changedFamilies: readonly DecisionFamily[]; readonly eventIds: ReadonlySet<string>;
  /** The option the team went with on each card that fired (its label, as the card named it), so a question can name the team's own answer, never another option's. */
  readonly answers?: ReadonlyMap<string, string>;
  /** The team's own changes per desk, one plain line each ("Business loan growth: Hold demand growth → Add 10% on top of demand growth"), so a question about its choices can name them. */
  readonly changes?: Readonly<Partial<Record<DecisionFamily, readonly string[]>>>;
}

/** "Business loan growth: A → B; Loan pricing: C → D" (three at most, then "and N more"), or null when no lines are known. */
const namedChanges = (lines: readonly string[] | undefined): string | null => {
  if (lines === undefined || lines.length === 0) return null;
  const shown = lines.slice(0, 3).join("; ");
  return lines.length > 3 ? `${shown}; and ${lines.length - 3} more` : shown;
};

/**
 * One reflection question per round, from the debrief guide's question slide (content.md §5), asked only when what it names happened
 * (loan decisions changed, that card fired for this seat); otherwise the general question.
 */
const ROUND_QUESTIONS: Readonly<Record<number, { readonly text: (c: ReflectionContext, solo: boolean) => string; readonly when: (c: ReflectionContext) => boolean }>> = {
  1: { text: (c) => { const named = namedChanges(c.changes?.loans); return named === null ? "What would have to be true for this year's growth to pay for itself?" : `You changed the loan book: ${named}. What would have to be true for this year's growth to pay for itself?`; }, when: (c) => c.changedFamilies.includes("loans") },
  2: { text: () => "Where did the fraud loss show up first: provision, charge-off or capital, and in which month?", when: (c) => c.eventIds.has("EV-07") },
  3: { text: (c, solo) => `Who paid for '${lowerFirst(c.answers?.get("EV-02") ?? "")}': ${solo ? "this year or next year" : "this year's team or next year's"}?`, when: (c) => c.eventIds.has("EV-02") && (c.answers?.get("EV-02") ?? "") !== "" },
  4: { text: () => "Which number did you check first when the run started, and was it the right one?", when: (c) => c.eventIds.has("EV-01") },
};
const GENERAL_QUESTION = "What surprised you this year, and which decision explains it?";

export const reflectionQuestion = (roundNo: number, context: ReflectionContext, solo = false): string => {
  const round = ROUND_QUESTIONS[roundNo];
  if (round !== undefined && round.when(context)) return round.text(context, solo);
  const all = namedChanges(Object.values(context.changes ?? {}).flat());
  return all === null ? GENERAL_QUESTION : `You changed: ${all}. ${GENERAL_QUESTION}`;
};

export function useReflection(teamId: string, roundId: string): { readonly reflection: Reflection | undefined; readonly save: (field: ReflectionField, value: string) => Promise<void>; readonly state: SaveState } {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useRoute("reflectionGet", { teamId, roundId });
  const [state, setState] = useState<SaveState>("idle");
  const save = useCallback(async (field: ReflectionField, value: string) => {
    setState("saving");
    const result = await api.call("reflectionPut", { teamId, roundId }, { [field]: value.trim() === "" ? null : value.trim() });
    if (!result.ok) { setState("failed"); toast(errorSentence(result.error)); return; }
    queryClient.setQueryData([ "reflectionGet", { teamId, roundId } ], result.value);
    setState("saved");
  }, [api, queryClient, roundId, teamId, toast]);
  return { reflection: query.data, save, state };
}

/** The option each fired card went with, by label: the team's answer, or the card's usual outcome when nobody answered. */
export const answerLabels = (events: readonly { readonly status: string; readonly card: { readonly eventId: string; readonly choices: readonly { readonly id: string; readonly label: string }[]; readonly defaultChoice: { readonly label: string } }; readonly response?: { readonly choiceId: string } | null | undefined }[]): ReadonlyMap<string, string> =>
  new Map(events.filter((e) => e.status !== "cancelled").map((e) => [e.card.eventId, e.card.choices.find((c) => c.id === e.response?.choiceId)?.label ?? e.card.defaultChoice.label] as const));

/** The team's change lines per changed desk, from the decisions it signed and the values the year opened with. */
export const reflectionChanges = (catalog: ContentCatalog, families: readonly DecisionFamily[], decisions: Decisions, seed: Partial<Decisions>): NonNullable<ReflectionContext["changes"]> =>
  Object.fromEntries(families.map((f) => [f, changeLines(changedEntries(catalog, f, decisions, seed), decisions, seed)] as const));
